import { useCallback, useEffect, useState } from "react";
import { UpgradeProfitsType } from "@/interface";
import useConnectWallet from "@/store/wallet/useConnectWallet";
import { formatUnits } from "ethers";

// 每次获取数据的条数限制
const limit = 100;

/**
 * useGetUpgradeProfitsByLimit - 分页获取升级算力收益数据
 *
 * 用于调用 OrderHistory 合约的 getUpgradeProfitsByLimit 方法分页获取用户升级算力收益。
 * 支持自动加载和滚动加载更多数据。
 */
const useGetUpgradeProfitsByLimit = () => {
    // 获取钱包地址和合约实例
    const {
        contractEvm: { OrderHistory },
        address,
    } = useConnectWallet();

    // 定义状态变量
    const [data, setData] = useState<UpgradeProfitsType[]>([]); // 升级算力收益数据
    const [upgradeProfitsLod, setLoading] = useState(false); // 加载状态
    const [start, setStart] = useState("1000000000000000000"); // 分页起始点
    const [end, setEnd] = useState(false); // 分页结束点
    const [ready, setReady] = useState(false); // 标记状态设置完毕

    // 获取升级算力收益数据的函数
    const getUpgradeProfitsByLimit = useCallback(async () => {
        if (OrderHistory) {
            try {
                setLoading(true);
                // 调用合约方法获取数据
                const res = await OrderHistory.getUpgradeProfitsByLimit(address, start, limit);
                setEnd(res.length < limit);
                // 如果返回数据不为空，更新分页起始点
                if (res.length) {
                    const index = Number(formatUnits(res[res.length - 1].index, 0)) - 1 || 0;
                    if (index <= 0) {
                        setStart("0");
                    } else {
                        setStart(index.toString());
                    }
                    // 将新数据追加到收益数据中
                    setData([...data, ...res]);
                } else {
                    setStart("0");
                }
            } catch (error) {
                console.log("useGetUpgradeProfitsByLimit", error);
            } finally {
                setLoading(false);
            }
        }
    }, [OrderHistory, address, data, start]);

    // 第一步：初始化状态
    useEffect(() => {
        if (OrderHistory) {
            setData([]);
            setStart("1000000000000000000");
            setEnd(false);
            setReady(true);
        }
    }, [OrderHistory]);

    // 第二步：当状态设置完后执行请求
    useEffect(() => {
        if (ready) {
            getUpgradeProfitsByLimit();
            setReady(false);
        }
    }, [getUpgradeProfitsByLimit, ready]);

    // 返回 Hook 的方法和状态
    return { getUpgradeProfitsByLimit, data, upgradeProfitsLod, end };
};

export default useGetUpgradeProfitsByLimit;